import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Input, Modal } from '@/components/ui';
import {
  Upload,
  Search,
  Image,
  Trash2,
  Copy,
  Check,
  FileImage,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  alt?: string;
  variants?: Record<string, string>;
  createdAt: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MediaPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const { data: media, isLoading } = useQuery<MediaItem[]>({
    queryKey: ['media', { search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const response = await api.get(`/admin/media?${params}`);
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });
      return api.post('/admin/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/media/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setSelectedItem(null);
    },
  });

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        uploadMutation.mutate(e.dataTransfer.files);
      }
    },
    [uploadMutation]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        uploadMutation.mutate(e.target.files);
        e.target.value = '';
      }
    },
    [uploadMutation]
  );

  const copyToClipboard = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Media Library</h1>
          <p className="text-gray-400 mt-1">Manage images and files</p>
        </div>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Upload className="h-4 w-4 mr-2" />
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Search */}
      <Card padding="md">
        <Input
          placeholder="Search files..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-4 w-4" />}
        />
      </Card>

      {/* Upload Zone */}
      {uploadMutation.isPending && (
        <Card padding="lg">
          <div className="flex items-center justify-center gap-3 py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
            <span className="text-gray-400">Uploading files...</span>
          </div>
        </Card>
      )}

      {uploadMutation.isError && (
        <Card padding="lg" className="border-red-500/50">
          <div className="flex items-center gap-3 text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Upload failed. Please try again.</span>
          </div>
        </Card>
      )}

      {/* Drop Zone / Grid */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={clsx(
          'relative rounded-xl border-2 border-dashed transition-colors',
          isDragging
            ? 'border-gold bg-gold/5'
            : 'border-midnight-100 bg-midnight-50'
        )}
      >
        {isDragging && (
          <div className="absolute inset-0 flex items-center justify-center bg-midnight/80 z-10 rounded-xl">
            <div className="text-center">
              <Upload className="h-12 w-12 text-gold mx-auto mb-2" />
              <p className="text-white font-medium">Drop files to upload</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
          </div>
        ) : media && media.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
            {media.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={clsx(
                  'relative aspect-square rounded-lg overflow-hidden bg-midnight-100 group transition-all',
                  selectedItem?.id === item.id && 'ring-2 ring-gold'
                )}
              >
                {item.mimeType.startsWith('image/') ? (
                  <img
                    src={item.url}
                    alt={item.alt || item.originalName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileImage className="h-8 w-8 text-gray-500" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-medium px-2 py-1 bg-black/50 rounded truncate max-w-full">
                    {item.originalName}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Image className="h-12 w-12 mb-4" />
            <p className="font-medium">No files yet</p>
            <p className="text-sm mt-1">Upload images to get started</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Files
            </Button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title="File Details"
        size="lg"
      >
        {selectedItem && (
          <div className="space-y-6">
            {/* Preview */}
            <div className="aspect-video rounded-lg overflow-hidden bg-midnight-100">
              {selectedItem.mimeType.startsWith('image/') ? (
                <img
                  src={selectedItem.url}
                  alt={selectedItem.alt || selectedItem.originalName}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FileImage className="h-16 w-16 text-gray-500" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Filename</p>
                <p className="text-white truncate">{selectedItem.originalName}</p>
              </div>
              <div>
                <p className="text-gray-400">Size</p>
                <p className="text-white">{formatFileSize(selectedItem.size)}</p>
              </div>
              <div>
                <p className="text-gray-400">Type</p>
                <p className="text-white">{selectedItem.mimeType}</p>
              </div>
              <div>
                <p className="text-gray-400">Uploaded</p>
                <p className="text-white">
                  {format(new Date(selectedItem.createdAt), 'MMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* URL */}
            <div>
              <p className="text-gray-400 text-sm mb-2">URL</p>
              <div className="flex items-center gap-2">
                <Input
                  value={selectedItem.url}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(selectedItem.url)}
                >
                  {copiedUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-midnight-100">
              <Button
                variant="ghost"
                onClick={() => {
                  if (confirm('Delete this file?')) {
                    deleteMutation.mutate(selectedItem.id);
                  }
                }}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button variant="outline" onClick={() => setSelectedItem(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
